import { z } from 'zod';
import { DRAWER_SEARCH_PARAM_ITEM_ID } from '@/constants/drawer-search';
import {
  normalizeOptionalSearchString,
  normalizeSearchString,
  parsePositiveIntegerSearchValue,
} from '@/lib/route-search';
import { loginRequestSchema, registerRequestSchema } from '@/types';

const optionalFilterSlugSchema = z.preprocess(normalizeSearchString, z.string().optional());
const optionalQuerySearchSchema = z.preprocess(normalizeOptionalSearchString, z.string().optional());

function normalizeMultiValueFilterSearchString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = Array.from(
    new Set(
      value
        .split(',')
        .map((entry) => normalizeSearchString(entry))
        .filter((entry): entry is string => Boolean(entry)),
    ),
  ).sort();

  return normalized.length > 0 ? normalized.join(',') : undefined;
}

const positiveIntegerSearchValueSchema = z.preprocess(
  parsePositiveIntegerSearchValue,
  z.number().int().positive().optional(),
);

export const itemFilterSearchSchema = z.object({
  query: optionalQuerySearchSchema,
  type: optionalFilterSlugSchema,
  ui: optionalFilterSlugSchema,
  tags: z.preprocess(normalizeMultiValueFilterSearchString, z.string().optional()),
});

export const itemListQuerySchema = itemFilterSearchSchema.extend({
  page: z.number().int().positive(),
});

export const itemListSearchSchema = itemFilterSearchSchema.extend({
  [DRAWER_SEARCH_PARAM_ITEM_ID]: positiveIntegerSearchValueSchema,
});

export const loginFormSchema = loginRequestSchema;
export const registerFormSchema = registerRequestSchema;

export type ItemFilterSearch = z.infer<typeof itemFilterSearchSchema>;
export type ItemListQuery = z.infer<typeof itemListQuerySchema>;
export type ItemListSearch = z.infer<typeof itemListSearchSchema>;
export type LoginFormValues = z.infer<typeof loginFormSchema>;
export type RegisterFormValues = z.infer<typeof registerFormSchema>;

export function normalizeItemFilterSearch(search: ItemFilterSearch): ItemFilterSearch {
  const query = normalizeOptionalSearchString(search.query);
  const type = normalizeSearchString(search.type);
  const tags = normalizeMultiValueFilterSearchString(search.tags);

  return {
    query,
    type,
    tags,
    ui: tags ? undefined : normalizeSearchString(search.ui),
  };
}

export function normalizeItemListSearch(search: ItemListSearch): ItemListSearch {
  const normalizedFilters = normalizeItemFilterSearch(search);

  return {
    ...normalizedFilters,
    [DRAWER_SEARCH_PARAM_ITEM_ID]: parsePositiveIntegerSearchValue(search[DRAWER_SEARCH_PARAM_ITEM_ID]),
  };
}
